import React, { useEffect, useRef, useState } from 'react';

interface RevealProps {
  children: React.ReactNode;
  /** Stagger offset in seconds, so siblings can cascade. */
  delay?: number;
  className?: string;
}

const prefersReducedMotion = () => typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Fades and lifts its children into view the first time they are scrolled to.
 *
 * Content is only ever hidden once we know we can reveal it again: if
 * IntersectionObserver is unavailable, motion is reduced, or the observer has
 * not fired within a second, the children render plainly. Nothing on the page
 * can end up permanently invisible because of an animation.
 */
const Reveal: React.FC<RevealProps> = ({ children, delay = 0, className = '' }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [armed, setArmed] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') return undefined;

    setArmed(true);

    const node = ref.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShown(true);
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px -12% 0px' },
    );
    if (node) observer.observe(node);

    // Safety net: never leave content hidden if the observer never fires.
    const fallback = window.setTimeout(() => setShown(true), 1200);

    return () => {
      observer.disconnect();
      window.clearTimeout(fallback);
    };
  }, []);

  const hidden = armed && !shown;

  return (
    <div
      ref={ref}
      className={`${className} transition-[opacity,transform] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
        hidden ? 'translate-y-5 opacity-0' : 'translate-y-0 opacity-100'
      }`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
};

Reveal.defaultProps = {
  delay: 0,
  className: '',
};

export default Reveal;
